import * as React from "react";
import { Check, X } from "lucide-react";

import { cn } from "@/lib/utils";

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { useSearchParams } from "react-router-dom";
import FilterLabel from "./FilterLabel";
import { Button } from "@/components/ui/button";
import { useSearchStore } from "@/stores/SearchStore";

const SearchFilterContext = React.createContext();

function FilterItem({ filterKeys, label, urlName, ...props }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { checkedList, setCheckedList } = React.useContext(SearchFilterContext);

  const isChecked = React.useMemo(
    () =>
      filterKeys.some((key) =>
        checkedList.some((item) => item?.keys?.includes(key) || item === key)
      ),
    [checkedList, filterKeys]
  );

  function setAllSearchParams(newSearchParams) {
    setSearchParams(newSearchParams);
    useSearchStore.setState({ searchPageSearchParams: newSearchParams });
  }

  function onClicked() {
    const newSearchParams = new URLSearchParams(searchParams);

    if (!isChecked) {
      filterKeys.forEach((key) => {
        newSearchParams.append(urlName, key); // Ensure `key` is a string
      });
      setCheckedList([...checkedList, { label, keys: filterKeys }]);
    } else {
      filterKeys.forEach((key) => {
        newSearchParams.delete(urlName, key);
      });
      setCheckedList(
        checkedList.filter((item) => item.label !== label || item === label)
      );
    }
    setAllSearchParams(newSearchParams);
  }
  return (
    <CommandItem value={label} onSelect={onClicked} {...props}>
      {label}
      <Check
        className={cn("ml-auto", isChecked ? "opacity-100" : "opacity-0")}
      />
    </CommandItem>
  );
}

function CheckedListButton({ filterKeys, label, className }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { checkedList, setCheckedList, urlName } =
    React.useContext(SearchFilterContext);

  const isChecked = React.useMemo(
    () =>
      filterKeys.some((key) =>
        checkedList.some((item) => item?.keys?.includes(key) || item === key)
      ),
    [checkedList, filterKeys]
  );
  function setAllSearchParams(newSearchParams) {
    setSearchParams(newSearchParams);
    useSearchStore.setState({ searchPageSearchParams: newSearchParams });
  }
  function onClicked() {
    const newSearchParams = new URLSearchParams(searchParams);

    if (!isChecked) {
      filterKeys.forEach((key) => {
        newSearchParams.append(urlName, key);
      });
      setCheckedList([...checkedList, { label, keys: filterKeys }]);
    } else {
      filterKeys.forEach((key) => {
        newSearchParams.delete(urlName, key);
      });
      setCheckedList(
        checkedList.filter((item) => item.label !== label || item === label)
      );
    }
    setAllSearchParams(newSearchParams);
  }
  return (
    <Button key={label} className={cn("h-8", className)} onClick={onClicked}>
      <X className="w-2 h-2" /> {label}
    </Button>
  );
}

function CheckedList({ className }) {
  const { checkedList } = React.useContext(SearchFilterContext);
  return (
    <div className={className}>
      {checkedList?.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {checkedList.map((item) => (
            <CheckedListButton
              key={item?.label || item}
              filterKeys={item?.keys || [item]}
              label={item?.label || item}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function SearchFilter({
  title,
  placeholder,
  items,
  urlName,
  topK,
}) {
  const [inputFocused, setInputFocused] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [searchParams] = useSearchParams();

  const checkedItemList = items.filter((item) =>
    (item?.keys || [item]).some((key) => searchParams.has(urlName, key))
  );
  const [checkedList, setCheckedList] = React.useState(checkedItemList);

  React.useEffect(() => {
    if (!searchParams.has(urlName)) {
      setCheckedList([]);
    }
  }, [searchParams, urlName]);

  const HideableCommandItem = (props) => {
    if (!inputFocused) return null;
    return <FilterItem {...props} />;
  };
  const HideableCommandEmpty = (props) => {
    if (!inputFocused) return null;
    return <CommandEmpty {...props} />;
  };
  return (
    <SearchFilterContext.Provider
      value={{ checkedList, setCheckedList, urlName }}
    >
      <div className="space-y-2">
        <FilterLabel>{title}</FilterLabel>
        <Command
          onFocus={() => setInputFocused(true)}
          onBlur={() => setInputFocused(false)}
        >
          <CommandInput
            placeholder={placeholder}
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <HideableCommandEmpty>No result found.</HideableCommandEmpty>
            <CommandGroup className="my-0 py-0">
              {topK
                ? items
                    .filter((item) => {
                      const label = (item?.label || item).toLowerCase();
                      const searchLower = search.toLowerCase();
                      return /^\d/.test(searchLower)
                        ? label.startsWith(searchLower)
                        : label.includes(searchLower);
                    })
                    .slice(0, 10)
                    .map((item) => (
                      <HideableCommandItem
                        key={item?.keys || item}
                        filterKeys={item?.keys ? item.keys : [item]}
                        label={item?.label || item}
                        urlName={urlName}
                      />
                    ))
                : items.map((item) => (
                    <HideableCommandItem
                      key={item?.keys || item}
                      filterKeys={item?.keys ? item.keys : [item]}
                      label={item?.label || item}
                      urlName={urlName}
                    />
                  ))}
            </CommandGroup>
          </CommandList>
        </Command>
        <CheckedList className="pt-2" />
      </div>
    </SearchFilterContext.Provider>
  );
}
